/* eslint-disable security/detect-object-injection */
import { Row } from '@tanstack/react-table';
import writeXlsxFile from 'write-excel-file';
import { IOrderHistoryLine, IOrderHistoryTableData } from '../actions/order-history/get-order-history.action';
import {
    XLSX_EXCLUDED_TABLE_COLUMNS,
    XLSX_FILE_NAME_ORDER_HISTORY,
    XLSX_SCHEDULE_LINE_COLUMN_CONFIRMED_DATE,
    XLSX_SCHEDULE_LINE_COLUMN_CREATED_ON,
    XLSX_SCHEDULE_LINE_COLUMN_NUMBER,
    XLSX_TABLE_KEY_MAPPING,
    XLSX_WORKSHEET_NAME
} from '../configuration';
import { IOrderHistoryResources } from '../modules/order-history';
import { ILocalizationState } from '../states/localization-state';
import { dateLocaleFormatter } from './format-date';
import { formatNumberLocaleStatic } from './format-number';
import { mapItemInvoiceStatusCodeToText, mapItemStatusCodeToText } from './map-item-status-code-to-text';
import { mapOverallDeliveryStatus, mapOverallInvoiceStatus } from './map-overall-status-code-to-text';

const excludedColumnKeys = new Set(XLSX_EXCLUDED_TABLE_COLUMNS);

const formatDateIfValid = (dateString: string, localizationState: ILocalizationState) => {
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : dateLocaleFormatter(localizationState).formatDate(date);
};

const formatOrderData = (order: Row<IOrderHistoryTableData>, localizationState: ILocalizationState, resources: IOrderHistoryResources) => {
    const formattedOrder: { [key: string]: string } = {};
    for (const key in order.original) {
        if (order.original.hasOwnProperty(key) && !excludedColumnKeys.has(key)) {
            const value = order.original[key];
            if (key.includes('Date') && typeof value === 'string' && value !== undefined) {
                // check for date values and format to localized date
                const formattedDate = formatDateIfValid(value, localizationState);
                if (formattedDate) {
                    formattedOrder[resources[XLSX_TABLE_KEY_MAPPING[key]] || key] = formattedDate;
                }
            } else if (key === 'StateLogistics') {
                // check for delivery status values and replace with delivery status translation
                const orderDeliveryStatusTitle = resources.orderHistoryOrderDeliveryStatusText;
                formattedOrder[orderDeliveryStatusTitle] = mapOverallDeliveryStatus(resources).getText(value);
            } else if (key === 'StateInvoice') {
                // check for invoice status values and replace with invoice status translation
                const orderInvoiceStatusTitle = resources.orderHistoryOrderInvoiceStatusText;
                formattedOrder[orderInvoiceStatusTitle] = mapOverallInvoiceStatus(resources).getText(value);
            } else {
                // replace technical keys with translation
                formattedOrder[resources[XLSX_TABLE_KEY_MAPPING[key]] || key] = value;
            }
        }
    }
    return formattedOrder;
};

const formatLineData = (line: IOrderHistoryLine, localizationState: ILocalizationState, resources: IOrderHistoryResources) => {
    const formattedLine: { [key: string]: string } = {};
    for (const key in line) {
        if (line.hasOwnProperty(key) && !excludedColumnKeys.has(key)) {
            const value = line[key];
            if (key.includes('Date') && typeof value === 'string' && value !== undefined) {
                // check for date values and format to localized date
                const formattedDate = formatDateIfValid(value, localizationState);
                if (formattedDate) {
                    formattedLine[resources[XLSX_TABLE_KEY_MAPPING[key]] || key] = formattedDate;
                }
            } else if (key === 'StateLogistics') {
                const lineDeliveryStatusTitle = resources.orderHistoryLineOrderStatusText;
                // check for delivery status values and replace with delivery status translation
                formattedLine[lineDeliveryStatusTitle] = mapItemStatusCodeToText(value, resources);
            } else if (key === 'StateInvoice') {
                const lineInvoiceStatusTitle = resources.orderHistoryLineInvoiceStatusText;
                // check for invoice status values and replace with invoice status translation
                formattedLine[lineInvoiceStatusTitle] = mapItemInvoiceStatusCodeToText(value, resources);
            } else if ((key.includes('Qty') || key.includes('NetValue')) && typeof value === 'string' && value !== undefined) {
                // check for net value and quantity values and format to localized numbers
                formattedLine[resources[XLSX_TABLE_KEY_MAPPING[key]] || key] = formatNumberLocaleStatic(value, localizationState);
            } else {
                // replace technical keys with translation
                formattedLine[resources[XLSX_TABLE_KEY_MAPPING[key]] || key] = value;
            }

            if (line.ScheduleLines) {
                line.ScheduleLines.forEach(scheduleLine => {
                    for (const key in scheduleLine) {
                        if (scheduleLine.hasOwnProperty(key) && !excludedColumnKeys.has(key)) {
                            const value = scheduleLine[key];
                            if (key.includes('Date') && typeof value === 'string' && value !== undefined) {
                                const formattedDate = formatDateIfValid(value, localizationState);
                                if (formattedDate) {
                                    formattedLine[resources[XLSX_TABLE_KEY_MAPPING[`ScheduleLine${key}`]]] = formattedDate;
                                }
                            } else if (key.includes('Qty') && typeof value === 'string' && value !== undefined) {
                                formattedLine[resources[XLSX_TABLE_KEY_MAPPING[key]] || key] = formatNumberLocaleStatic(
                                    value,
                                    localizationState
                                );
                            } else {
                                formattedLine[resources[XLSX_TABLE_KEY_MAPPING[key]] || key] = value;
                            }
                        }
                    }
                });
            }
        }
    }
    return formattedLine;
};

export const exportToExcel = async (
    filteredData: Row<IOrderHistoryTableData>[],
    localizationState: ILocalizationState,
    resources: IOrderHistoryResources,
    selectedArray: string[]
) => {
    const allowedKeys = new Set<string>();

    selectedArray.forEach((key: string) => {
        const mappingKey = XLSX_TABLE_KEY_MAPPING[key];
        if (mappingKey && resources[mappingKey]) {
            allowedKeys.add(resources[mappingKey]);
        }
    });

    const formattedList = filteredData.flatMap(order => {
        const formattedOrder = formatOrderData(order, localizationState, resources);
        return order.original.OrderLines.map((line: IOrderHistoryLine) => {
            const item = {
                ...formattedOrder,
                ...formatLineData(line, localizationState, resources)
            };

            if (allowedKeys.size === 0) {
                return [];
            }

            // keep only the keys present in allowedKeys
            return Object.fromEntries(Object.entries(item).filter(([key]) => allowedKeys.has(key)));
        });
    });

    // Check for all possible keys
    const allKeys = formattedList.reduce((keySet, item) => {
        Object.keys(item).forEach(key => keySet.add(key));
        return keySet;
    }, new Set<string>());

    const headerKeys = Array.from(allKeys);

    const HEADER_ROW = headerKeys.map(key => ({
        column: key,
        type: String,
        value: key
    }));

    // flatten whole table and explicitly assign schedule line content
    // TODO Redo
    const formattedData = formattedList.map(item => {
        return HEADER_ROW.map(header => {
            const key = header.column;
            if (item.hasOwnProperty(XLSX_SCHEDULE_LINE_COLUMN_NUMBER)) {
                if (key === XLSX_SCHEDULE_LINE_COLUMN_CREATED_ON) {
                    return {
                        name: key,
                        value: item[key]
                    };
                } else if (key === XLSX_SCHEDULE_LINE_COLUMN_CONFIRMED_DATE) {
                    return {
                        name: resources[XLSX_TABLE_KEY_MAPPING[`ScheduleLine${key}`]],
                        value: item[key]
                    };
                } else {
                    return {
                        name: key,
                        value: item[key]
                    };
                }
            } else {
                return {
                    name: key,
                    value: item[key]
                };
            }
        });
    });

    const columnWidthPadding = 2;

    // Calculate column width
    const columns = HEADER_ROW.map(header => {
        const maxLength = Math.max(
            header.value.length,
            ...formattedData.map(item => {
                const cell = item.find(cell => cell.name === header.column);
                return cell ? cell?.value?.length : 0;
            })
        );
        return { width: maxLength + columnWidthPadding };
    });

    // Combine header and data for writeXlsxFile
    const sheetData = [HEADER_ROW, ...formattedData];

    await writeXlsxFile(sheetData, {
        columns,
        fileName: XLSX_FILE_NAME_ORDER_HISTORY,
        sheet: XLSX_WORKSHEET_NAME
    });
};
